import React, { useState } from 'react';

import { FILLS, FONTS, GRADIENTS, STROKES } from '../../styles/tokens';
import { getAbsoluteImageUrl } from '../../utils/formatters';

interface AvatarProps {
  url?: string | null;
  name: string;
  size?: number;
  /**
   * Fallback (no-logo) treatment. `'gold'` is the copper-gold gradient disc
   * used for people (users, hosts). `'neutral'` is a quiet tonal chip —
   * used where a solid-gold disc would spend the One-Lamp accent on a
   * secondary mark (e.g. brand provenance on a detail page).
   */
  fallbackTone?: 'gold' | 'neutral';
}

const Avatar: React.FC<AvatarProps> = ({ url, name, size = 36, fallbackTone = 'gold' }) => {
  const [imageFailed, setImageFailed] = useState(false);

  if (url && !imageFailed) {
    return (
      <img
        src={getAbsoluteImageUrl(url)}
        alt={name}
        onError={() => setImageFailed(true)}
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          objectFit: 'cover',
          flexShrink: 0,
        }}
      />
    );
  }

  const initials = name ? name.charAt(0).toUpperCase() : 'U';
  const isNeutral = fallbackTone === 'neutral';

  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: isNeutral ? FILLS.chip : GRADIENTS.copperGold,
        border: isNeutral ? `1px solid ${STROKES.subtle}` : undefined,
        color: isNeutral ? FILLS.textLight : FILLS.page,
        fontSize: '14px',
        fontWeight: 600,
        fontFamily: FONTS.body,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {initials}
    </div>
  );
};

export default Avatar;
